/**
 * analytics.js — GA4 helper with DPDPA consent gate.
 *
 * Flow:
 *  1. ``index.html`` loads gtag.js with ``consent='denied'`` for analytics_storage
 *     before any tracking calls (Google's "Consent Mode v2" default).
 *  2. On first visit, CookieBanner asks the user. If they pick "Accept all" or
 *     toggle Analytics on, ``grantAnalyticsConsent()`` is called → gtag flips
 *     analytics_storage to ``granted`` and all queued page_views fire.
 *  3. ``trackPageView(path)`` fires on every route change from AppRouter.
 *  4. ``trackEvent(name, params)`` is the catch-all for conversion events
 *     (signup, login, trial_start, payment_success, intro_request).
 *
 * If ``REACT_APP_GA4_ID`` is unset, every call is a no-op — safe for dev/tests.
 */

const GA4_ID = process.env.REACT_APP_GA4_ID || '';

const _isEnabled = () => !!GA4_ID && typeof window !== 'undefined' && typeof window.gtag === 'function';

const CONSENT_KEY = 'ibclub.consent.v1';

/** Read consent from localStorage. */
const readConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

/** Sync GA4 consent state to whatever the user picked in CookieBanner. */
export const syncConsentFromStorage = () => {
  if (!_isEnabled()) return;
  const c = readConsent();
  const analytics = c?.analytics ? 'granted' : 'denied';
  const marketing = c?.marketing ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
};

/** Called by CookieBanner after the user picks accept/reject/customize. */
export const updateConsent = ({ analytics, marketing }) => {
  if (!_isEnabled()) return;
  window.gtag('consent', 'update', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });
};

/** Track a SPA page view. Call on every route change. */
export const trackPageView = (path) => {
  if (!_isEnabled()) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
    // Attach audience signals so GA4 can segment without GTM round-trip
    user_tier: _audience.user_tier,
    trial_state: _audience.trial_state,
    is_returning_visitor: _audience.is_returning_visitor,
  });
};

/**
 * Track a custom event.
 *
 * Recommended names (keep them stable so GA4 funnels work):
 *   - sign_up            (after /auth/register success)
 *   - login              (after /auth/login success)
 *   - trial_start        (fired alongside sign_up — auto by backend)
 *   - purchase           (after Razorpay verify success; params: {value, currency, transaction_id, tier})
 *   - generate_lead      (intro request, newsletter signup)
 *   - search             (community/business directory filter applied)
 *   - share              (social share button click)
 */
export const trackEvent = (name, params = {}) => {
  if (!_isEnabled()) return;
  window.gtag('event', name, params);
};

/** Identify the logged-in user (sets user_id for cross-device stitching). */
export const setUserId = (userId) => {
  if (!_isEnabled() || !userId) return;
  window.gtag('config', GA4_ID, { user_id: userId });
};

// ---------------------------------------------------------------------------
// dataLayer audience signals
//
// Three values exposed to GTM so the marketing team can build sharper
// remarketing audiences without code changes:
//   - user_tier            : 'free' | 'basic' | 'premium' | 'elite' | 'anonymous'
//   - trial_state          : 'trial_active' | 'trial_expired' | 'paid' | 'none'
//   - is_returning_visitor : boolean
//
// We push these to ``window.dataLayer`` so GTM picks them up via Data Layer
// Variables, AND attach them to every ``page_view`` event so GA4 sees them
// directly as event params (no GTM round-trip needed).
// ---------------------------------------------------------------------------

const _audience = {
  user_tier: 'anonymous',
  trial_state: 'none',
  is_returning_visitor: false,
};

const _pushDataLayer = (obj) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(obj);
};

/** Push the user's tier + trial state onto dataLayer for GTM audiences. */
export const setUserContext = ({ user_tier, trial_state } = {}) => {
  if (user_tier !== undefined) _audience.user_tier = user_tier || 'anonymous';
  if (trial_state !== undefined) _audience.trial_state = trial_state || 'none';
  _pushDataLayer({
    event: 'user_context',
    user_tier: _audience.user_tier,
    trial_state: _audience.trial_state,
  });
};

/** First-visit marker. Sets a localStorage timestamp and returns whether the
 *  user has been here before. Pushes ``is_returning_visitor`` to dataLayer
 *  exactly once per page-load. */
export const markVisitor = () => {
  if (typeof window === 'undefined') return false;
  let isReturning = false;
  try {
    const k = 'ibclub.first_seen';
    const firstSeen = localStorage.getItem(k);
    if (firstSeen) {
      isReturning = true;
    } else {
      localStorage.setItem(k, String(Date.now()));
    }
  } catch { /* private mode / blocked storage */ }
  _audience.is_returning_visitor = isReturning;
  _pushDataLayer({
    event: 'visitor_seen',
    is_returning_visitor: isReturning,
  });
  return isReturning;
};

/** Read the current audience snapshot (used by trackPageView to attach
 *  signals to every page_view event). */
export const getAudience = () => ({ ..._audience });
